/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { AnvilorFormsAppStore } from "../anvilor-forms-app-store.service";
import { AnvilorFormsAppStoreContext } from "../anvilor-forms-app-store.service";
import { AnvilorFormDocumentLoader } from "./anvilor-form-document-loader";
import { SubmissionDataLoader } from "./submission-data-loader";
import { RuiInitializer } from "./rui-initializer";
import { Observable } from "rxjs";
import { combineLatest } from "rxjs";
import { debounceTime } from "rxjs";
import { filter } from "rxjs";
import { shareReplay } from "rxjs";
import { RuiInitStatus } from "../anvilor-forms-app.state";
import { LoadStatus } from "../anvilor-forms-app.state";
import { map } from "rxjs/operators";
import { asSignal } from "@anvilor/ngx-core-utils";
import { ClientDataLoader } from "./client-data-loader";
import { ValidationLoadingChecks } from "./validation-loading-checks";
import { LinkPatchesLoading } from "./link-patches";
import { AnvilorFormsAppAutosave } from "../autosave/app-autosave";

/**
 * All logic related to loading the document and submission data, as well
 * as the RUI initialization.
 *
 * It delegates the loading/initialization of specific features to various
 * helper classes, and helps to orchestrate the general behavior amongst those
 * features loading together.
 */
export class FormAppLoading {
  private autosave: AnvilorFormsAppAutosave;
  private anvilorFormLoader: AnvilorFormDocumentLoader;
  private submissionDataLoader: SubmissionDataLoader;
  private loadingChecks: ValidationLoadingChecks;
  private ruiInitializer: RuiInitializer;
  private store: AnvilorFormsAppStore;
  private linkPatches: LinkPatchesLoading;
  readonly clientDataLoader: ClientDataLoader;

  readonly ruiInitStatus$: Observable<RuiInitStatus>;
  readonly editConfigLoadStatus$: Observable<LoadStatus>;
  readonly isLoadingForm$: Observable<boolean>;
  readonly formIsLoaded$: Observable<boolean>;
  readonly isLoadingSubmissionData$: Observable<boolean>;
  readonly isFullyLoaded$: Observable<boolean>;
  readonly isLoadingEditConfig$: Observable<boolean>;
  readonly editConfigLoaded$: Observable<boolean>;
  readonly waitingForEditConfig$: Observable<boolean>;

  constructor(autosave: AnvilorFormsAppAutosave, ctx: AnvilorFormsAppStoreContext) {
    this.autosave = autosave;
    this.anvilorFormLoader = new AnvilorFormDocumentLoader(ctx);
    this.clientDataLoader = new ClientDataLoader(ctx);
    this.submissionDataLoader = new SubmissionDataLoader(ctx);
    this.ruiInitializer = new RuiInitializer(ctx);
    this.loadingChecks = new ValidationLoadingChecks(ctx);
    this.linkPatches = new LinkPatchesLoading(ctx);
    this.store = ctx.store;

    this.ruiInitStatus$ = this.store.select((state) => state.rui_initialization_status);
    this.editConfigLoadStatus$ = this.store.select(
      (state) => state.edit_config_load_status
    );
    this.isLoadingForm$ = this.ruiInitStatusIs("loading_form");
    this.isLoadingSubmissionData$ = this.ruiInitStatusIs("loading_data");
    this.formIsLoaded$ = this.ruiInitStatusIs("loading_data", "complete");
    this.isFullyLoaded$ = this.ruiInitStatusIs("complete");
    this.isLoadingEditConfig$ = this.editConfigLoadStatusIs("active");
    this.editConfigLoaded$ = this.editConfigLoadStatusIs("complete");

    this.waitingForEditConfig$ = combineLatest([
      this.formIsLoaded$,
      this.isLoadingEditConfig$,
    ]).pipe(
      map(([formLoaded, loadingEditConfig]) => formLoaded && loadingEditConfig),
      debounceTime(400),
      filter((waiting) => waiting),
      asSignal()
    );
  }

  load() {
    const submissionUxConfig$ = this.anvilorFormLoader
      .loadSubmissionUxConfig()
      .pipe(shareReplay());
    const submissionData$ = this.submissionDataLoader.loadSubmissionData();
    const ruiInit$ = this.ruiInitializer.initializeRui(
      submissionUxConfig$, // Alternatively we could pass from the state?
      submissionData$,
      this.linkPatches.responseDataPatches$,
      this.autosave.getAutosaveData()
    );
    const clientDataLoading$ =
      this.clientDataLoader.clientDataLoadingFromSubmissionUxConfig(
        submissionUxConfig$
      );
    this.store.subscribeTo(clientDataLoading$);
    this.store.subscribeTo(ruiInit$);
    this.loadingChecks.watchForInvalidLoad();
  }

  private ruiInitStatusIs(...statuses: RuiInitStatus[]): Observable<boolean> {
    return this.ruiInitStatus$.pipe(map((v) => statuses.includes(v)));
  }

  private editConfigLoadStatusIs(...statuses: LoadStatus[]): Observable<boolean> {
    return this.editConfigLoadStatus$.pipe(map((v) => statuses.includes(v)));
  }
}
